import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { Monitor, Clock, CheckCircle2, Loader2, Wifi, LogOut, ChevronRight, ChefHat, UtensilsCrossed } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import API_URL from "@/utils/api";

const API = API_URL;

export default function PublicOrderDisplay() {
  const [time, setTime] = useState(new Date());
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["public-orders"],
    queryFn: async () => {
      const res = await axios.get(`${API}/orders/display/public`);
      return res.data;
    },
    refetchInterval: 5000,
  });

  const branding = data?.branding;

  // Sync Branding Colors
  useEffect(() => {
    // Check multiple locations for the primary color
    const primaryColor = 
      data?.branding?.primary_color || 
      data?.primary_color || 
      data?.branch?.branding?.primary_color;

    if (primaryColor) {
      document.documentElement.style.setProperty('--primary', primaryColor);
      
      // Derive RGB for opacity and glow effects
      if (primaryColor.startsWith('#')) {
        const r = parseInt(primaryColor.slice(1, 3), 16);
        const g = parseInt(primaryColor.slice(3, 5), 16);
        const b = parseInt(primaryColor.slice(5, 7), 16);
        document.documentElement.style.setProperty('--primary-rgb', `${r}, ${g}, ${b}`);
      }
    } else {
      // Default Conexo Deep Blue if nothing found
      document.documentElement.style.setProperty('--primary', '#1a40d0'); 
      document.documentElement.style.setProperty('--primary-rgb', '26, 64, 208');
    }

    // Update Title and Favicon
    if (data?.company_name) {
      document.title = `${data.company_name} — Pantalla de Pedidos`;
    }
    
    const logo = data?.logo || data?.branding?.logo_url;
    if (logo) {
      const favicon = document.querySelector('link[rel="icon"]');
      if (favicon) favicon.href = logo;
    }
  }, [data]);

  if (isLoading) {
    return (
      <div className="h-screen bg-[#050505] flex flex-col items-center justify-center text-primary font-mono" style={{ color: 'var(--primary)' }}>
        <Loader2 className="w-16 h-16 animate-spin mb-4" />
        <span className="text-xl font-black tracking-[0.4em] animate-pulse uppercase">Sincronizando Sistema...</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="h-screen bg-red-950/20 flex flex-col items-center justify-center text-red-500 font-mono p-10 text-center">
        <Wifi className="w-20 h-20 mb-6 animate-bounce" />
        <h1 className="text-4xl font-black mb-4 tracking-tighter uppercase italic">Error de Conexión</h1>
        <p className="text-xl max-w-md opacity-70">El terminal no puede alcanzar el núcleo del sistema. Reconectando en breve...</p>
      </div>
    );
  }

  const preparing = data?.orders?.filter((o) => ["preparing", "pending"].includes(o.status)) || [];
  const ready = data?.orders?.filter((o) => o.status === "ready") || [];
  const companyName = data?.company_name || "CONEXO POS";
  const branchName = data?.branch_name || "CENTRAL HUB";
   

  return (
    <div className="min-h-screen bg-[#020202] text-white flex flex-col font-sans selection:bg-primary selection:text-black overflow-hidden relative">
      
      {/* HUD: Cyberpunk Header */}
      <header className="relative z-10 px-8 py-6 border-b border-white/5 bg-black/40 backdrop-blur-xl flex justify-between items-center shadow-2xl">
        <div className="flex items-center gap-6">
          <div className="relative">
              <div className="relative w-16 h-16 flex items-center justify-center bg-transparent border-2 border-primary/30 rounded-2xl overflow-hidden" style={{ borderColor: 'rgba(var(--primary-rgb), 0.3)' }}>
                {data?.logo || data?.branding?.logo_url ? (
                  <img src={data?.logo || data?.branding?.logo_url} alt={companyName} className="w-12 h-12 object-contain" />
                ) : (
                  <UtensilsCrossed className="w-10 h-10 text-primary" style={{ color: 'var(--primary)' }} strokeWidth={2.5} />
                )}
              </div>
          </div>
          <div>
            <h1 className="text-[clamp(1.5rem,4vw,2.5rem)] font-black leading-none tracking-tighter uppercase italic translate-y-1">
              {companyName} <span className="text-primary tracking-normal not-italic ml-2 opacity-80">· {branchName}</span>
            </h1>
            <div className="flex items-center gap-2 mt-2">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-ping" />
              <span className="text-[clamp(0.6rem,1.5vw,0.8rem)] font-bold text-green-500/80 tracking-widest uppercase flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full" /> 
                Conectado al Sistema
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-8">
          <div className="flex flex-col items-end">
            <div className="flex items-center gap-3 text-primary font-mono text-[clamp(2.2rem,5vw,3.8rem)] font-black leading-none tracking-tighter drop-shadow-[0_0_10px_rgba(var(--primary-rgb),0.3)]">
              {time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </div>
            <span className="text-[10px] text-primary/50 uppercase font-black tracking-widest mt-1">Sincronizado</span>
          </div>
          
          {user?.role === "screen" && (
            <button 
              onClick={() => { logout(); navigate("/staff"); }}
              className="p-4 rounded-2xl bg-white/5 hover:bg-white/10 text-white/40 hover:text-red-400 transition-all group border border-white/5"
              title="Cerrar Sesión Pantalla"
            >
              <LogOut className="w-6 h-6 group-hover:scale-110 transition-transform" />
            </button>
          )}
        </div>
      </header>

      {/* CORE DISPLAY MATRIX */}
      <main className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-0 relative z-10">
        
        {/* LEFT COMPARTMENT: PREPARING (NEON BLUE AESTHETIC) */}
        <section className="p-8 lg:p-12 border-r border-white/5 bg-gradient-to-br from-black to-slate-900/40 overflow-y-auto custom-scrollbar">
          <div className="flex items-center gap-4 mb-10 pb-6 border-b border-white/10 sticky top-0 bg-transparent backdrop-blur-sm z-20">
            <div className="w-10 h-10 rounded-xl border border-slate-700 flex items-center justify-center overflow-hidden">
               <div className="w-full h-full bg-slate-800 flex items-center justify-center">
                  <div className="w-2 h-2 bg-slate-500 rounded-full animate-pulse" />
               </div>
            </div>
            <h2 className="text-[clamp(1.2rem,3vw,2rem)] font-black tracking-[0.2em] text-slate-400 uppercase italic">En Preparación</h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-6">
            {preparing.map((order) => (
              <div 
                key={order.order_id} 
                className="group relative"
              >
                <div className="absolute inset-0 bg-blue-500/0 group-hover:bg-blue-500/10 rounded-3xl transition-all duration-500 blur-2xl" />
                <div className="relative p-6 lg:p-8 rounded-3xl bg-white/[0.03] border border-white/5 backdrop-blur-md flex flex-col items-center justify-center transition-all duration-500 hover:border-blue-500/30 hover:scale-[1.03] shadow-xl hover:shadow-blue-500/5 group-hover:-translate-y-1">
                  <span className="text-slate-500 text-[10px] font-black uppercase tracking-[0.2em] mb-1 opacity-50">PEDIDO</span>
                  <span className="text-[clamp(1.5rem,4vw,3.2rem)] font-black text-white leading-none tracking-tighter font-mono group-hover:text-blue-400 transition-colors">
                    #{order.order_id.slice(-3).toUpperCase()}
                  </span>
                </div>
              </div>
            ))}
            {preparing.length === 0 && (
              <div className="col-span-full py-20 text-center opacity-20 flex flex-col items-center">
                <Monitor className="w-20 h-20 mb-4 text-slate-700" />
                <p className="text-xl font-black uppercase tracking-[0.5em] italic">Cola Vacía</p>
                <div className="mt-4 flex gap-2">
                   <div className="w-12 h-1 bg-slate-800 rounded-full animate-pulse" />
                   <div className="w-4 h-1 bg-slate-800 rounded-full animate-pulse" />
                </div>
              </div>
            )}
          </div>
        </section>

        {/* RIGHT COMPARTMENT: READY (VIBRANT GOLD AESTHETIC) */}
        <section className="p-8 lg:p-12 bg-primary/[0.01] overflow-y-auto custom-scrollbar">
          <div className="flex items-center gap-4 mb-10 pb-6 border-b border-primary/20 sticky top-0 bg-transparent backdrop-blur-sm z-20" style={{ borderColor: 'rgba(var(--primary-rgb), 0.2)' }}>
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center shadow-[0_0_20px_rgba(var(--primary-rgb),0.4)]" style={{ backgroundColor: 'var(--primary)', boxShadow: '0 0 20px rgba(var(--primary-rgb), 0.4)' }}>
               <CheckCircle2 className="w-7 h-7 text-black animate-bounce" />
            </div>
            <h2 className="text-[clamp(1.2rem,3vw,2rem)] font-black tracking-[0.2em] text-primary uppercase italic" style={{ color: 'var(--primary)' }}>Listo para Recoger</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 lg:gap-10">
            {ready.map((order) => (
              <div 
                key={order.order_id} 
                className="group relative animate-in zoom-in duration-700 slide-in-from-right-10"
              >
                <div className="absolute inset-0 bg-primary/20 rounded-[48px] blur-3xl animate-pulse" />
                <div className="relative p-10 lg:p-16 rounded-[48px] bg-gradient-to-br from-primary via-primary to-primary/80 flex flex-col items-center justify-center shadow-2xl transform group-hover:scale-105 transition-all duration-700 border-b-8 border-black/20">
                  <div className="absolute top-8 flex items-center gap-3 px-6 py-2 bg-black/10 rounded-full backdrop-blur-md border border-white/10">
                     <span className="text-[10px] font-black text-black/90 uppercase tracking-[0.3em] flex items-center gap-2">
                        <span className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-black opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-black"></span>
                        </span>
                        ENTREGA INMEDIATA
                     </span>
                  </div>
                  <span className="text-[clamp(4.5rem,12vw,10.5rem)] font-black text-black leading-none tracking-tighter drop-shadow-xl font-mono">
                    #{order.order_id.slice(-3).toUpperCase()}
                  </span>
                  <div className="mt-6 flex flex-col items-center gap-2">
                    <span className="text-black font-black uppercase text-[clamp(1rem,2vw,1.4rem)] tracking-[0.1em] italic">¡Buen Provecho!</span>
                    <div className="w-16 h-1.5 bg-black/20 rounded-full" />
                  </div>
                </div>
              </div>
            ))}
            {ready.length === 0 && (
              <div className="col-span-full h-full flex flex-col items-center justify-center py-20 opacity-10">
                <Monitor className="w-32 h-32 mb-6" />
                <p className="text-2xl font-black uppercase tracking-[0.5em] italic">Buscando pedidos...</p>
                <div className="flex gap-1 mt-4">
                   {[1,2,3].map(i => <div key={i} className="w-4 h-4 rounded-full bg-white animate-pulse" style={{ animationDelay: `${i*0.2}s` }} />)}
                </div>
              </div>
            )}
          </div>
        </section>

      </main>

      {/* FOOTER: SYSTEM TELEMETRY (CLEANED) */}
      <footer className="relative z-10 px-8 py-6 bg-transparent flex justify-center items-center">
        <div className="text-white/5 font-black italic uppercase tracking-widest text-[8px] flex items-center gap-3">
          SISTEMA DE GESTIÓN TPV <span className="opacity-50">·</span> {companyName}
        </div>
      </footer>

      {/* SCANLINE EFFECTS & OVERLAYS */}
      <div className="fixed inset-0 pointer-events-none bg-[linear-gradient(rgba(18,16,16,0)_50%,rgba(0,0,0,0.1)_50%),linear-gradient(90deg,rgba(255,0,0,0.03),rgba(0,255,0,0.01),rgba(0,0,255,0.03))] z-50 bg-[length:100%_4px,3px_100%] opacity-40" />
      <div className="fixed inset-0 pointer-events-none bg-[radial-gradient(circle_at_center,transparent_0%,rgba(0,0,0,0.2)_100%)] z-40" />
    </div>
  );
}
